import React, { useEffect, useState } from 'react';
import {
  Container,
  ThemeTable,
  ThemeInput,
  DogByIDModal,
  DogsModal,
  UserDetailModal,
  EditDogModal,
} from '../../components/components';
import {
  useLazyGetDogByIDQuery,
  useSearchDogsQuery,
} from '../../services/dogsApi';
import { useLazyGetUserByIdQuery } from '../../services/users';
import { MedicineBoxOutlined } from '@ant-design/icons';
import { PiDogFill } from 'react-icons/pi';
import { FaUser, FaEdit } from 'react-icons/fa';
import { happy_dog } from '../../assets/images/images';

const Medication = () => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [isDogModalOpen, setIsDogModalOpen] = useState(false);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalInitialTab, setEditModalInitialTab] = useState('3');

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      if (searchInput !== search) {
        setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Always filter by medication
  const { data, isLoading } = useSearchDogsQuery({
    name: search,
    currentPage,
    resultPerPage,
    medicationFilter: 'has_medication',
  });

  const [getDogByID, { isLoading: fetchingDog }] = useLazyGetDogByIDQuery();
  const [getUserById, { isLoading: fetchingOwner }] = useLazyGetUserByIdQuery();
  const [dogData, setDogData] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [userDetailData, setUserDetailData] = useState(null);

  useEffect(() => {
    const totalFromShape =
      data?.data?.[0]?.total ??
      data?.data?.total ??
      (Array.isArray(data?.data) ? data.data.length : undefined) ??
      0;
    setTotal(totalFromShape);
  }, [data, isLoading]);

  const onOpenDog = async id => {
    setDogData(null);
    const res = await getDogByID(id);
    setDogData(res?.data?.data || null);
    setIsDogModalOpen(true);
  };

  const onOpenOwner = async ownerId => {
    setOwnerData(null);
    const res = await getUserById({ id: ownerId });
    setOwnerData(res?.data?.data || null);
    setIsOwnerModalOpen(true);
  };

  const handleViewUserDetails = data => {
    setUserDetailData(data);
    setIsOwnerModalOpen(false);
    setIsUserDetailModalOpen(true);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setCurrentPage(1);
  };

  const onOpenEditModal = async (id, tab = '3') => {
    setDogData(null);
    setEditModalInitialTab(tab);
    const res = await getDogByID(id);
    setDogData(res?.data?.data || null);
    setIsEditModalOpen(true);
  };

  const getMedicationType = record => {
    const types = [];
    if (record.regular_medication?.yes) {
      types.push('Regular');
    }
    if (record.medication_during_stay?.yes) {
      types.push('During Stay');
    }
    return types.join(', ') || 'None';
  };

  const renderMedicationDetails = record => {
    const hasRegular =
      record.regular_medication?.yes && record.regular_medication?.details;
    const hasStay =
      record.medication_during_stay?.yes &&
      record.medication_during_stay?.details;

    if (!hasRegular && !hasStay) {
      return <span className="text-gray-400">No details provided</span>;
    }

    return (
      <div className="flex flex-col gap-2 text-left">
        {hasRegular && (
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase">
              Regular:
            </span>
            <p className="text-sm text-gray-700">
              {record.regular_medication.details}
            </p>
          </div>
        )}
        {hasStay && (
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase">
              During Stay:
            </span>
            <p className="text-sm text-gray-700">
              {record.medication_during_stay.details}
            </p>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      render: (image, record) => (
        <div className="flex justify-center">
          <img
            src={image || happy_dog}
            className="w-20 h-20 rounded-full cursor-pointer object-cover"
            alt="avatar"
            onClick={() => onOpenDog(record._id)}
          />
        </div>
      ),
    },
    {
      title: 'Dog Name',
      dataIndex: 'dog_name',
      key: 'dog_name',
      render: (text, record) => (
        <p
          key={`dog-name-${record._id}`}
          className="text-center font-semibold text-lg text-black cursor-pointer hover:underline"
          onClick={() => onOpenDog(record._id)}
        >
          {text}
        </p>
      ),
    },
    {
      title: 'Breed',
      dataIndex: 'breed',
      key: 'breed',
      render: (text, record) => (
        <p key={`breed-${record._id}`} className="text-center">
          {text}
        </p>
      ),
    },
    {
      title: 'Medication Type',
      dataIndex: '_id',
      key: 'medication_type',
      render: (_, record) => (
        <div className="flex justify-center">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {getMedicationType(record)}
          </span>
        </div>
      ),
    },
    {
      title: 'Medication Details',
      dataIndex: '_id',
      key: 'medication_details',
      render: (_, record) => renderMedicationDetails(record),
    },
    {
      title: 'Actions',
      dataIndex: '_id',
      key: 'actions',
      render: (id, record) => (
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={() => onOpenDog(id)}
            className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
          >
            <PiDogFill size={16} />
            View Dog
          </button>
          <button
            onClick={() => onOpenEditModal(id, '3')}
            className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
          >
            <FaEdit size={14} />
            Edit Medical
          </button>
          {record.owner_id && (
            <button
              onClick={() => onOpenOwner(record.owner_id)}
              className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
            >
              <FaUser size={14} />
              View Owner
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Container
      selected={'9'}
      header={
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
              <MedicineBoxOutlined className="text-white text-lg" />
            </div>
            <p className="ml-2 font-semibold text-2xl">Medication</p>
          </div>

          <div className="flex items-center gap-4 w-4/12">
            <ThemeInput
              placeholder={'Search dogs by name'}
              className={'flex-1'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      }
    >
      <ThemeTable
        columns={columns}
        data={
          Array.isArray(data?.data?.[0]?.dogs)
            ? data.data[0].dogs
            : Array.isArray(data?.data?.items)
              ? data.data.items
              : Array.isArray(data?.data)
                ? data.data
                : []
        }
        pagination={{ total, setCurrentPage, setResultPerPage }}
        loader={isLoading}
        entityName="dogs"
        emptyText={
          search
            ? `No dogs on medication match "${search}".`
            : 'No dogs are on medication right now.'
        }
      />

      <DogByIDModal
        isOpen={isDogModalOpen}
        onClose={() => setIsDogModalOpen(false)}
        data={dogData}
        isLoading={fetchingDog}
        onEditDog={() => {
          setIsDogModalOpen(false);
          setIsEditModalOpen(true);
        }}
        onViewOwner={onOpenOwner}
        onDeleteSuccess={() => setIsDogModalOpen(false)}
        zIndex={1010}
      />

      <DogsModal
        isOpen={isOwnerModalOpen}
        onClose={() => setIsOwnerModalOpen(false)}
        data={ownerData}
        isLoading={fetchingOwner}
        onViewUserDetails={handleViewUserDetails}
        onViewDog={onOpenDog}
        zIndex={1020}
      />

      <UserDetailModal
        isOpen={isUserDetailModalOpen}
        onClose={() => setIsUserDetailModalOpen(false)}
        data={userDetailData}
      />

      <EditDogModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        data={dogData}
        initialTab={editModalInitialTab}
        zIndex={1010}
      />
    </Container>
  );
};

export default Medication;
